import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { formatBookingStatus, bookingStatusDotClass } from "../lib/bookingStatus";
import { formatTripRange, bookingTimeZoneLabel, formatLocalTimestamp } from "../lib/dateLocal";
import { formatMoney } from "../lib/currencyDisplay";

function Row({ label, children, testId }) {
  if (children == null || children === "") return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[var(--zc-border)] last:border-0" data-testid={testId}>
      <span className="text-[var(--zc-text-3)] text-sm shrink-0">{label}</span>
      <span className="text-sm text-right font-medium min-w-0 break-words">{children}</span>
    </div>
  );
}

export default function HostBookingDetails({ booking, open, onClose }) {
  if (!booking) return null;

  const b = booking;
  const snap = b.vehicle_snapshot || {};
  const currency = (b.currency || "USD").toUpperCase();
  const persona = b.renter_persona || {};
  const completedTrips = persona.completed_trips ?? 0;

  const afterHoursPickup = Number(b.after_hours_pickup_fee || 0);
  const afterHoursReturn = Number(b.after_hours_return_fee || 0);
  const hasAfterHours = afterHoursPickup > 0 || afterHoursReturn > 0;

  const oneWay = !!b.one_way;
  const oneWayFee = Number(b.one_way_fee || 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="host-booking-details">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {snap.year} {snap.make} {snap.model}
          </DialogTitle>
          <DialogDescription className="sr-only">Booking details</DialogDescription>
        </DialogHeader>

        <div className="space-y-0">
          <Row label="Status" testId="hbd-status">
            <span className="inline-flex items-center gap-2">
              <span className={"dot " + bookingStatusDotClass(b.status)} />
              {formatBookingStatus(b)}
            </span>
          </Row>

          <Row label="Booking ID" testId="hbd-booking-id">
            <span className="font-mono text-xs">{b.booking_id}</span>
          </Row>

          <Row label="Vehicle ID" testId="hbd-vehicle-id">
            <span className="font-mono text-xs">{b.vehicle_id}</span>
          </Row>

          <Row label="Booked on" testId="hbd-created-at">
            {formatLocalTimestamp(b.created_at, b.booking_timezone) || "—"}
          </Row>

          <Row label="Trip dates" testId="hbd-dates">
            <div className="text-xs">
              <div>{formatTripRange(b.start_date, b.end_date) || "—"}</div>
              {bookingTimeZoneLabel(b) && (
                <div className="text-[var(--zc-text-3)] mt-0.5">{bookingTimeZoneLabel(b)}</div>
              )}
            </div>
          </Row>

          <Row label="Trip cost" testId="hbd-total">
            {formatMoney(b.total, currency)}
          </Row>
        </div>

        {(hasAfterHours || oneWay) && (
          <div>
            <div className="eyebrow mb-2 mt-2">Extras</div>
            <div className="space-y-0">
              {afterHoursPickup > 0 && (
                <Row label="After-hours pickup" testId="hbd-ah-pickup">
                  {formatMoney(afterHoursPickup, currency)}
                </Row>
              )}
              {afterHoursReturn > 0 && (
                <Row label="After-hours return" testId="hbd-ah-return">
                  {formatMoney(afterHoursReturn, currency)}
                </Row>
              )}
              {oneWay && (
                <Row label="Different location return" testId="hbd-one-way">
                  {oneWayFee > 0 ? formatMoney(oneWayFee, currency) : "Yes"}
                </Row>
              )}
            </div>
          </div>
        )}

        <div>
          <div className="eyebrow mb-2 mt-2">Renter</div>
          <div className="space-y-0">
            <Row label="Name" testId="hbd-renter-name">
              {b.renter_display?.label || b.renter_name || "—"}
            </Row>
            <Row label="Completed trips" testId="hbd-renter-trips">
              {completedTrips}
            </Row>
            {/* KAN-231: hide blank Rating until renter/host ratings ship */}
            {persona.avg_rating != null && Number(persona.rating_count) > 0 && (
              <Row label="Rating" testId="hbd-renter-rating">
                {Number(persona.avg_rating).toFixed(1)} ({persona.rating_count})
              </Row>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
